import React, { useRef, useState } from 'react';
import * as cls from './request-a-demo-form.module.css';
import { IMG_PATH, ROUTES_PATHS } from '../../../constants';
import { Section } from '../../../ui/components/section';
import { Typography } from '../../../ui/components/typography';
import { Button } from '../../../ui/components/button';

const items = [
  {
    text: 'Appliance-level insights from smart-meter data - no hardware upgrades required.',
  },
  {
    text: 'Dashboards, APIs, and branded apps that cut call-centre load.',
  },
  {
    text: 'Proven engagement results: utilities reaching 70% MAU monthly.',
  },
];

export const RequestADemoForm = () => {
  const initialFields = {
    company: '',
    email: '',
    jobTitle: '',
    message: '',
    name: '',
    lastName: '',
  };

  const [loading, setLoading] = useState(false);
  const [fields, setFields] = useState(initialFields);
  const [errors, setErrors] = useState([]);
  const [feedback, setFeedback] = useState(null);

  const startTime = useRef(Date.now());
  const botDetected = useRef(false);

  const resetForm = () => setFields(initialFields);

  const handleOnChange = (name) => (e) => {
    e.preventDefault();
    setFields({ ...fields, [name]: e.target.value });
  };

  const emailSuccess = () => {
    setFeedback({
      message: 'A member of the team will be in touch.',
      title: 'Thank you!',
      type: 'success',
    });
  };

  const emailFailure = () => {
    setFeedback({
      message:
        'An error has occurred. Please try again later or contact us directly at enquiries@voltaware.com',
      title: 'Submission Failed',
      type: 'error',
    });
  };

  const validate = () => {
    const errors = [];
    if (fields.name === '') errors.push('name');
    if (
      fields.email === '' ||
      !fields.email.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)
    )
      errors.push('email');
    if (fields.company === '') errors.push('company');

    setErrors(errors);

    if (errors.length) {
      const element = document.querySelector(`.${cls.section}`);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
      return false;
    }
    return true;
  };

  const sendEmail = () => {
    if (botDetected.current) return;
    setLoading(true);

    const data = {
      replyto: `${fields.name} <${fields.email}>`,
      subject: "Request a Demo - New enquiry from Voltaware's website",
      botDetected: botDetected.current,
      text: `Name: ${fields.name}
  Email: ${fields.email}
  Company: ${fields.company}
  ${fields.jobTitle ? `Job Title: ${fields.jobTitle}` : ''}
  ${fields.message ? `Message: ${fields.message}` : ''}
  `,
    };

    const config = {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    };

    fetch('/.netlify/functions/send-email', config)
      .then((res) => {
        if (res.ok) {
          resetForm();
          return emailSuccess();
        }

        return emailFailure();
      })
      .catch((err) => {
        return emailFailure();
      })
      .finally(() => {
        setLoading(false);
        setErrors([]);
      });
  };

  const handleFormSubmit = (e) => {
    e.preventDefault();
    if (Date.now() - startTime.current < 1500) {
      botDetected.current = true;
    }
    if (!validate()) return;
    sendEmail();
  };

  return (
    <>
      <Section backgroundContrast classNameRoot={cls.section}>
        <div className={cls.wrapper}>
          <div className={cls.text__wrapper}>
            <Typography tag="h1">Request a Demo</Typography>
            <Typography>
              <b>
                See how Voltaware helps utilities engage customers and unlock
                new revenue.
              </b>
            </Typography>

            <ul className={cls.list}>
              {items.map((item, index) => (
                <li key={index}>
                  <img alt="" src={`${IMG_PATH}/ico-check.svg`} />
                  <Typography>{item.text}</Typography>
                </li>
              ))}
            </ul>
          </div>

          <form className={cls.form} noValidate onSubmit={handleFormSubmit}>
            <Typography tag="h2">Request a Demo</Typography>
            <fieldset className={cls.form__fieldset} disabled={loading}>
              <div>
                <label>
                  Name*
                  <input
                    autoComplete="on"
                    className={errors.includes('name') ? cls.error : ''}
                    name="name"
                    onChange={handleOnChange('name')}
                    placeholder="Name"
                    required
                    type="text"
                    value={fields.name}
                  />
                  {errors.includes('name') && (
                    <span className={cls.error}>Please enter your name.</span>
                  )}
                </label>
              </div>
              <div>
                <label>
                  Work Email*
                  <input
                    autoComplete="on"
                    className={errors.includes('email') ? cls.error : ''}
                    name="email"
                    onChange={handleOnChange('email')}
                    placeholder="name@company.com"
                    required
                    type="email"
                    value={fields.email}
                  />
                  {errors.includes('name') && (
                    <span className={cls.error}>
                      Please enter a valid work email.
                    </span>
                  )}
                </label>
              </div>
              <div>
                <label>
                  Company / Organisation*
                  <input
                    autoComplete="on"
                    className={errors.includes('company') ? cls.error : ''}
                    name="company"
                    onChange={handleOnChange('company')}
                    placeholder="Company / Organisation"
                    required
                    type="text"
                    value={fields.company}
                  />
                  {errors.includes('name') && (
                    <span className={cls.error}>
                      Please enter your company or organisation.
                    </span>
                  )}
                </label>
              </div>
              <div>
                <label>
                  Job Title (optional)
                  <input
                    name="jobTitle"
                    onChange={handleOnChange('jobTitle')}
                    placeholder="Job Title"
                    type="text"
                    value={fields.jobTitle}
                  />
                </label>
              </div>
              <div>
                <label>
                  Message
                  <textarea
                    name="message"
                    onChange={handleOnChange('message')}
                    placeholder="Tell us about your goals, use case, and what you’d like to see in the demo."
                    value={fields.message}
                  ></textarea>
                </label>
              </div>
              <div className={cls.hidden}>
                <label>
                  Last Name
                  <input
                    autoComplete="off"
                    name="lastName"
                    onChange={(e) => {
                      botDetected.current = true;
                      handleOnChange('lastName')(e);
                    }}
                    placeholder="Last Name"
                    type="text"
                    value={fields.lastName}
                  />
                </label>
              </div>

              <Typography className={cls.form__caption}>
                By submitting this form, you agree to be contacted by Voltaware
                regarding your enquiry. You can read our{' '}
                <a
                  href={ROUTES_PATHS.TERMS_AND_CONDITIONS}
                  rel="noopener noreferrer"
                  target="_blank"
                >
                  Terms & Conditions
                </a>{' '}
                and{' '}
                <a
                  href={ROUTES_PATHS.PRIVACY_POLICY}
                  rel="noopener noreferrer"
                  target="_blank"
                >
                  Privacy Policy
                </a>{' '}
                below.
              </Typography>

              <Button loading={loading} type="submit">
                Request My Demo
              </Button>

              {feedback && (
                <div className={cls.feedback}>
                  {feedback.type === 'success' ? (
                    <>
                      <Typography className={cls.feedback__title}>
                        <img alt="" src={`${IMG_PATH}/ico-check.svg`} />
                        {feedback.title}
                      </Typography>
                      <Typography className={cls.feedback__message}>
                        {feedback.message}
                      </Typography>
                    </>
                  ) : (
                    <>
                      <Typography className={cls.feedback__title}>
                        {feedback.title}
                      </Typography>
                      <Typography className={cls.feedback__message}>
                        {feedback.message}
                      </Typography>
                    </>
                  )}
                </div>
              )}
            </fieldset>
          </form>
        </div>
      </Section>
    </>
  );
};
